import { Injectable, inject } from '@angular/core';
import { classes } from 'polytype';
import type { Observable } from 'rxjs';

import type {
    ProjectAssetCoverage,
    ProjectEvidenceSnapshot,
    ProjectFeedbackImproveLoop,
    ProjectLifecycleStage,
} from '@app/assets/projects/project-home-page/project-home-page.component';
import { PROJECT_PATH } from '@app/shared/api/api-route.config';
import type { ItemResponse, ListResponse, StatusResponse } from '@app/shared/api/crud.service';
import { WithCrudService } from '@app/shared/api/crud.service';
import { RestService } from '@app/shared/api/rest.service';
import type {
    ProjectAdapter,
    ProjectBuildStatus,
    ProjectContents,
    ProjectEnvironmentVariablesDto,
    ProjectMemberRoleAdapter,
    ProjectTemplate,
    ProjectTemplateProvider,
} from '@app/shared/project/project.model';

@Injectable({
    providedIn: 'root',
})
export class ProjectService extends classes(WithCrudService<ProjectAdapter>) {
    protected endpoint = PROJECT_PATH;

    protected readonly api = inject(RestService);

    /** What deleting the project together with its contents would delete. */
    getContents(id: number, showError = true): Observable<ItemResponse<ProjectContents>> {
        return this.api.get<ItemResponse<ProjectContents>>(
            `${this.endpoint}${id}/contents`,
            showError,
        );
    }

    /** Deletes the project and everything in it. */
    deleteWithContents(id: number, showError = true): Observable<StatusResponse> {
        return this.api.delete<StatusResponse>(
            `${this.endpoint}${id}?deleteContents=true`,
            showError,
        );
    }

    getBySlug(slug: string, showError = true): Observable<ItemResponse<ProjectAdapter>> {
        return this.api.get<ItemResponse<ProjectAdapter>>(
            `${this.endpoint}slug/${slug}`,
            showError,
        );
    }

    listTemplates(): Observable<ListResponse<ProjectTemplate>> {
        return this.api.get<ListResponse<ProjectTemplate>>(`${this.endpoint}templates`, false);
    }

    createFromTemplate(body: {
        templateSlug: string;
        provider: ProjectTemplateProvider;
        name: string;
        description: string;
    }): Observable<ItemResponse<ProjectAdapter>> {
        return this.api.post<ItemResponse<ProjectAdapter>>(`${this.endpoint}from_template`, body);
    }

    getBuildStatus(slug: string): Observable<ItemResponse<ProjectBuildStatus>> {
        return this.api.get<ItemResponse<ProjectBuildStatus>>(
            `${this.endpoint}slug/${slug}/build`,
            false,
        );
    }

    getLifecycle(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectLifecycleStage[]>> {
        return this.api.get<ItemResponse<ProjectLifecycleStage[]>>(
            `${this.endpoint}slug/${slug}/lifecycle`,
            showError,
        );
    }

    getEvidenceSnapshot(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectEvidenceSnapshot[]>> {
        return this.api.get<ItemResponse<ProjectEvidenceSnapshot[]>>(
            `${this.endpoint}slug/${slug}/evidence_snapshot`,
            showError,
        );
    }

    getAssetCoverage(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectAssetCoverage[]>> {
        return this.api.get<ItemResponse<ProjectAssetCoverage[]>>(
            `${this.endpoint}slug/${slug}/asset_coverage`,
            showError,
        );
    }

    getFeedbackImproveLoop(
        slug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectFeedbackImproveLoop>> {
        return this.api.get<ItemResponse<ProjectFeedbackImproveLoop>>(
            `${this.endpoint}slug/${slug}/feedback_improve_loop`,
            showError,
        );
    }

    updateGeneralAccess(
        body: { projectSlug: string; generalAccessRole: string | null },
        showError = true,
    ): Observable<ItemResponse<ProjectAdapter>> {
        return this.api.patch<ItemResponse<ProjectAdapter>>(
            `${this.endpoint}general_access`,
            body,
            showError,
        );
    }

    /** The environment variables of the project; only the project's admins can read them. */
    getEnvironmentVariables(
        projectSlug: string,
        showError = true,
    ): Observable<ItemResponse<ProjectEnvironmentVariablesDto>> {
        return this.api.getWithParams<ItemResponse<ProjectEnvironmentVariablesDto>>(
            `${this.endpoint}environment_variables`,
            { projectSlug },
            showError,
        );
    }

    updateEnvironmentVariables(body: {
        projectSlug: string;
        environmentVariables: string | null;
    }): Observable<ItemResponse<ProjectEnvironmentVariablesDto>> {
        return this.api.patch<ItemResponse<ProjectEnvironmentVariablesDto>>(
            `${this.endpoint}environment_variables`,
            body,
        );
    }

    listMemberRoles(projectSlug: string): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.getWithParams<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles`,
            { projectSlug },
        );
    }

    updateProjectMemberRoles(body: {
        projectSlug: string;
        userId?: number | null;
        teamId?: number | null;
        projectRoleIds: number[];
    }): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.patch<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles`,
            body,
        );
    }

    requestProjectAccess(body: {
        projectSlug: string;
        projectRoleIds: number[];
    }): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.post<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles/request_access`,
            body,
        );
    }

    approveProjectAccess(body: {
        projectMemberRoleIds: number[];
    }): Observable<ListResponse<ProjectMemberRoleAdapter>> {
        return this.api.post<ListResponse<ProjectMemberRoleAdapter>>(
            `${this.endpoint}member_roles/approve_access`,
            body,
        );
    }

    rejectProjectAccess(body: {
        projectMemberRoleIds: number[];
    }): Observable<ItemResponse<{ status: string }>> {
        return this.api.post<ItemResponse<{ status: string }>>(
            `${this.endpoint}member_roles/reject_access`,
            body,
        );
    }
}
